<template>
  <header class="root">
    <section class="general" :class="{ 'focus-search': focusSearch }" :style="headerStyle">
      <button id="nav" @click="showEventList">&#xf022;</button>
      <popover-inline class="active-race-event" :title="raceButtonTitle" prefer="dn" :img="raceEventImageUrl" :style="`--tl:${raceButtonTitle.length}`">
        <race-events-list />
        <template v-slot:alternate>
          <race-event-summary :race-event="activeRaceEvent" />
        </template>
      </popover-inline>
      <search-bar
        :include-race-events="false"
        :show-recent="false"
        :show-result-list="false"
        placeholder="Search participants"
        @focus="focusSearch = true"
        @complete="focusSearch = false"
        @query="filterParticipants"
      />
    </section>
    <section class="main menu" />
  </header>

  <main :class="{ 'lock-scroll': lockScroll }">
    <router-view class="root" v-slot="{ Component, route }">
      <transition :name="route.meta.transition || 'nav-default'">
        <component :is="Component" />
      </transition>
    </router-view>
  </main>

  <loading-spinner v-if="loading && isLoggedIn" />
</template>

<style src="@/assets/colors.scss" lang="scss"></style>
<style src="@/assets/style.scss" lang="scss"></style>
<style src="@/assets/animation.css" lang="css"></style>
<style src="@/assets/fonts.css" lang="css"></style>
<style src="@/assets/forms.scss" lang="scss"></style>
<style src="@/assets/button.scss" lang="scss"></style>
<style src="@/assets/controls.scss" lang="scss"></style>
<style src="@/assets/popover.scss" lang="scss"></style>

<style lang="scss">
:root {
  --nav-width: 180px;
  --header-height: 44px;
  --header-padding: 0.65em;
  --header-menu-height: 22px;
  --header-size: calc(2 * var(--header-padding) + var(--header-height) + var(--header-menu-height));
  --nav-button-size: 56px;

  #app {
    height: -webkit-fill-available;
  }

  .lock-scroll {
    background: red;
  }

  main {
    text-color: var(--text-color);
    min-height: -webkit-fill-available;
    overscroll-behavior: contain;
    box-sizing: border-box;
    width: calc(100vw - 12px);
    padding: 0;
    padding-top: var(--header-size);
    margin-left: 0;
    margin-right: 0;

    .root {
      margin: 1rem auto 0;
      padding: 0 1em;
      height: -webkit-fill-available;
      box-sizing: border-box;
      max-width: 100vw;
    }
  }
}

header.root {
  position: fixed;
  z-index: 15;
  background: var(--body-bgcolor);
  top: 0;
  left: 0;
  right: 0;

  &:last-of-type {
    box-shadow: 0 2px 8px rgb(0 0 0 / 50%),
                0 2px 30px rgb(0 0 0 / 80%);
  }

  & > *:not(:last-child) {
    margin-right: 8px;
  }

  .general {
    display: flex;
    align-items: stretch;
    height: var(--header-height);
    padding-top: var(--header-padding);

    & > *:not(.search-bar) {
      transition: width 0.4s ease, opacity 0.4s ease;
    }
  }

  .main.menu {
    margin-left: 1rem;
    padding-bottom: 0.25rem;
    height: var(--header-menu-height);
  }

  .active-race-event {
    width: var(--nav-width);
    padding-right: 1rem;

    button {
      width: stretch;
      height: 100%;
      margin: 0;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      background: transparent;
      border: none;
      box-shadow: none;
      color: var(--text-color);
      text-align: left;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      transition: background 0.14s ease;

      font-size: calc(1 / var(--tl) * var(--nav-width));
      font-size: clamp(0.6em, calc(1 / var(--tl) * var(--nav-width)), 1em);

      &:hover {
        background: var(--accent-color);
      }

      &:after {
        content: "\F0DD";
        font-family: "Font Awesome 5 Free";
        font-weight: 900;
        display: inline-block;
        transform: translateY(-3px);
        pointer-events: none;
        margin-left: 4px;
      }

      img {
        margin: 0 8px;
      }

      span {
        height: auto !important;
      }
    }
  }

  .search-bar {
    flex: 1;
    transition: width 1s ease;

    input {
      color: var(--input-color);
      background: var(--popover-abgcolor);
    }
  }
}

header {
  button#nav {
    border: none;
    box-shadow: none;
    font-family: 'Font Awesome 5 Free';
    font-size: 18px;
    color: var(--text-color);
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 54px;
    min-width: auto;
    transition: text-shadow 200ms ease;

    span {
      height: auto !important;
    }
  }

  button#nav:not(:active) {
    background: transparent;
  }

  button#nav:hover {
    text-shadow: 0 0 22px var(--text-color);
  }
}

@supports (-webkit-backdrop-filter: none) or (backdrop-filter: none) {
  header .general::before {
    content: '.';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: var(--raceEventImage);
    background-position: 50% 50%;
    filter: blur(50px);
    opacity: 0.4;
    pointer-events: none;
  }
}

@media screen and (max-width: 768px) {
  :root {
    --header-menu-height: 0px;
  }

  button#nav {
    position: fixed;
    bottom: 40vh;
    right: 34px;
    height: var(--nav-button-size) !important;
    width: var(--nav-button-size);
    min-width: auto;
    border-radius: 50%;
    background: var(--button-bgcolor) !important;
    box-shadow: 0 1px 15px rgba(0,0,0,0.5) !important;
    opacity: 0.8;
    backdrop-filter: blur(20px);
  }

  header .main.menu {
    display: none;
  }
}

@media screen and (max-width: 820px) {
  header.root {
    .general.focus-search {
      #nav,
      .active-race-event {
        opacity: 0;
      }

      .search-bar {
        position: fixed;
        top: var(--header-padding);
        left: var(--header-padding);
        right: var(--header-padding);
        width: auto;
        z-index: 100;

        input {
          background: var(--popover-abgcolor);
        }
      }

      > *:not(.search-bar) {
        width: 0;
        min-width: 0;
        padding-left: 0;
        padding-right: 0;
        overflow: hidden;
      }
    }
  }
}

.empty-state {
  margin: 2rem auto;
  max-width: 520px;
  text-align: center;
  color: var(--text-dim1-color);
}
</style>

<script>
import { toRef } from 'vue'
import eventbus from '@/eventbus.js'
import { isLoggedInRef } from '@/lib/authentication.js'
import SearchBar from '@/components/search-bar.vue'
import RaceEventsList from '@/components/race-events-list.vue'
import RaceEventSummary from '@/components/race-event-summary.vue'
import cableHandling from '@/lib/mixins/cable.js'

export default {
  name: 'App',

  components: {
    SearchBar,
    RaceEventsList,
    RaceEventSummary,
  },

  mixins: [
    cableHandling,
  ],

  provide() {
    return {
      app: this,
      activeRaceEvent: toRef(this, 'activeRaceEvent'),
      canHandleEscape: (component) => {
        this._escapeHandlers.push(component)
      },
      cannotHandleEscape: (component) => {
        const i = this._escapeHandlers.indexOf(component)
        if (i >= 0) this._escapeHandlers.splice(i, 1)
      },
    }
  },

  data() {
    return {
      lockScroll: false,
      focusSearch: false,
      _escapeHandlers: [],
    }
  },

  computed: {
    isLoggedIn() {
      return isLoggedInRef
    },

    activeRaceEvent() {
      const reid = this.sharedStore.state.activeRaceEventId
      if (!reid || !this.sharedStore.state.raceEvent[reid]) {
        return { id: undefined }
      }

      return this.sharedStore.state.raceEvent[reid]
    },

    loading() {
      return this.sharedStore.state.loadingEntities.length > 0
    },

    raceButtonTitle() {
      return this.activeRaceEvent?.name || 'Select an Event'
    },

    raceEventImageUrl() {
      return this.activeRaceEvent?.imageUrl
    },

    headerStyle() {
      return `--raceEventImage: url(${this.raceEventImageUrl});`
    },
  },

  mounted() {
    window.addEventListener('keydown', this.keydown, true)
    eventbus.on('loginDidSucceed', this.subscribeToChannels)
    this.subscribeToChannels()
  },

  unmounted() {
    window.removeEventListener('keydown', this.keydown, true)
    eventbus.removeListener('loginDidSucceed', this.subscribeToChannels)
  },

  methods: {
    subscribeToChannels() {
      this.$cable.subscribe({ channel: 'ControlChannel' })
    },

    showEventList(e) {
      e?.preventDefault()
      this.$router.push({ name: 'events' })
    },

    filterParticipants(query) {
      eventbus.emit('participantSearchQuery', query)
    },

    keydown(e) {
      if (e.key !== 'Escape') return

      for (let i = this._escapeHandlers.length - 1; i >= 0; i--) {
        const handler = this._escapeHandlers[i]
        if (handler.escapePressed && handler.escapePressed(e)) {
          e.preventDefault()
          e.stopPropagation()
          return
        }
      }
    },
  },
}
</script>
